import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Plus, CalendarDays, Clock, Users, Check, X, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  pending: 'bg-[#E9C46A] text-[#2D241D]',
  confirmed: 'bg-[#2D6A4F] text-white',
  cancelled: 'bg-red-100 text-red-600',
  completed: 'bg-[#0A422D] text-white',
};

export const ReservationsTab = ({ reservations = [], onRefresh }) => {
  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reservations/${id}?status=${status}`);
      onRefresh();
    } catch (e) { console.error(e); }
  };

  const today = new Date().toISOString().split('T')[0];
  const upcoming = reservations.filter(r => r.date >= today && r.status !== 'cancelled');
  const past = reservations.filter(r => r.date < today || r.status === 'cancelled');

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-[#2D241D]">Reservations ({upcoming.length} a venir)</h3>
            <p className="text-xs text-[#6E5E52]">Gerez les reservations de table en ligne</p>
          </div>
        </div>
      </div>

      {/* Upcoming */}
      <div className="space-y-3">
        {upcoming.length === 0 && (
          <div className="text-center py-8 text-[#6E5E52] bg-white rounded-2xl border border-[#E5DCD3]">
            <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p>Aucune reservation a venir</p>
          </div>
        )}
        {upcoming.map(res => (
          <div key={res.id} className="bg-white rounded-2xl p-4 border border-[#E5DCD3]" data-testid={`res-card-${res.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-[#2D241D]">{res.name}</span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[res.status] || statusColors.pending}`}>
                {res.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-3 text-sm text-[#6E5E52] mb-3">
              <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {res.date}</span>
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {res.time}</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {res.guests} pers.</span>
            </div>
            {(res.phone || res.email) && (
              <div className="flex flex-wrap gap-3 text-xs text-[#6E5E52] mb-3">
                {res.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {res.phone}</span>}
                {res.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {res.email}</span>}
              </div>
            )}
            {res.notes && <p className="text-xs text-[#6E5E52] italic mb-3">"{res.notes}"</p>}

            {res.status === 'pending' && (
              <div className="flex gap-2">
                <Button onClick={() => updateStatus(res.id, 'confirmed')} size="sm"
                  className="flex-1 bg-[#2D6A4F] hover:bg-[#245E44] text-white rounded-xl text-xs"
                  data-testid={`confirm-res-${res.id}`}>
                  <Check className="w-3 h-3 mr-1" /> Confirmer
                </Button>
                <Button onClick={() => updateStatus(res.id, 'cancelled')} size="sm" variant="outline"
                  className="flex-1 border-red-300 text-red-500 hover:bg-red-50 rounded-xl text-xs"
                  data-testid={`cancel-res-${res.id}`}>
                  <X className="w-3 h-3 mr-1" /> Refuser
                </Button>
              </div>
            )}
            {res.status === 'confirmed' && (
              <Button onClick={() => updateStatus(res.id, 'completed')} size="sm"
                className="w-full bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl text-xs"
                data-testid={`complete-res-${res.id}`}>
                <Check className="w-3 h-3 mr-1" /> Marquer termine
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Past */}
      {past.length > 0 && (
        <div className="mt-6">
          <h4 className="text-sm font-medium text-[#6E5E52] mb-2">Historique ({past.length})</h4>
          <div className="space-y-2">
            {past.slice(0, 10).map(res => (
              <div key={res.id} className="bg-white rounded-xl p-3 border border-[#E5DCD3] opacity-60 flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-[#2D241D]">{res.name}</span>
                  <span className="text-xs text-[#6E5E52] ml-2">{res.date} {res.time} - {res.guests} pers.</span>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[res.status] || ''}`}>
                  {res.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
